import * as i0 from '@angular/core';
import { EventEmitter, inject, ElementRef, ChangeDetectorRef, Injector, Directive, Input, Output, HostListener, Component, ChangeDetectionStrategy, ViewChild, NgModule } from '@angular/core';
import * as i2 from '@simpl/element-ng/autocomplete';
import { SiAutocompleteDirective, SiAutocompleteListboxDirective, SiAutocompleteOptionDirective } from '@simpl/element-ng/autocomplete';
import { ReplaySubject, isObservable, of, BehaviorSubject } from 'rxjs';
import { Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NgControl } from '@angular/forms';
import { getOverlay } from '@simpl/element-ng/common';
import { map } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { SiTranslateModule } from '@simpl/element-ng/translate';

class SiTypeaheadSorting {
    sortMatches(matches) {
        // Sort the matches,
        // first is the option and query an exact match.
        // then according to whether it is matching in the beginning,
        // then whether it matches the entire untokenized query.
        // then according to how many unique separate matches it contains.
        // then according to how many unique matches it contains.
        // then according to how many matches it contains.
        return matches.sort((matchA, matchB) => {
            if (matchA.stringMatch || matchB.stringMatch) {
                return matchA.stringMatch ? -1 : 1;
            }
            if (matchA.atBeginning) {
                return !matchB.atBeginning ? -1 : this.compareMatches(matchA, matchB);
            }
            else {
                return matchB.atBeginning ? 1 : this.compareMatches(matchA, matchB);
            }
        });
    }
    compareMatchesNumbers(matchA, matchB) {
        return (matchB.uniqueSeparateMatches - matchA.uniqueSeparateMatches ||
            matchB.uniqueMatches - matchA.uniqueMatches ||
            matchB.matches - matchA.matches);
    }
    compareMatches(matchA, matchB) {
        if (matchA.matchesEntireQuery) {
            return !matchB.matchesEntireQuery ? -1 : this.compareMatchesNumbers(matchA, matchB);
        }
        else {
            return matchB.matchesEntireQuery ? 1 : this.compareMatchesNumbers(matchA, matchB);
        }
    }
}

class SiTypeaheadDirective {
    static overlayPositions = [
        {
            overlayX: 'start',
            overlayY: 'top',
            originX: 'start',
            originY: 'bottom',
            offsetY: 2,
            offsetX: -12
        },
        {
            overlayX: 'start',
            overlayY: 'bottom',
            originX: 'start',
            originY: 'top',
            offsetY: -4,
            offsetX: -12
        },
        {
            overlayX: 'end',
            overlayY: 'top',
            originX: 'end',
            originY: 'bottom',
            offsetY: 2,
            offsetX: -12
        },
        {
            overlayX: 'end',
            overlayY: 'bottom',
            originX: 'end',
            originY: 'top',
            offsetY: -4,
            offsetX: -12
        }
    ];
    /**
     * Set the options of the typeahead.
     * Has to be either an Array or an Observable of an Array
     * of options (string or object)
     */
    siTypeahead;
    /**
     * Turns on/off the processing (searching and sorting) of the typeahead options.
     * Is used when searching and sorting is done externally.
     */
    typeaheadProcess = true;
    /**
     * Makes the typeahead scrollable and sets its height.
     * Uses {@link typeaheadOptionsInScrollableView} and {@link typeaheadScrollableAdditionalHeight}.
     */
    typeaheadScrollable = false;
    /**
     * If {@link typeaheadScrollable} is `true`, defines the number of items visible at once.
     */
    typeaheadOptionsInScrollableView = 10;
    /**
     * Defines the maximum number of items added into the DOM. Default is 20 and 0 means unlimited.
     */
    typeaheadOptionsLimit = 20;
    /**
     * If {@link typeaheadScrollable} is `true`, defines the number of additional pixels
     * to be added the the bottom of the typeahead to show users that it is scrollable.
     */
    typeaheadScrollableAdditionalHeight = 13;
    /**
     * Defines the index of the item which should automatically be selected.
     */
    typeaheadAutoSelectIndex = 0;
    /**
     * Defines whether the typeahead can be closed using escape.
     */
    typeaheadCloseOnEsc = true;
    /**
     * Defines whether the host value should be cleared when a value is selected.
     */
    typeaheadClearValueOnSelect = false;
    /**
     * Defines the number of milliseconds to wait before displaying a typeahead after the host was
     * focused or a value inputted.
     */
    typeaheadWaitMs = 0;
    /**
     * Defines the number of characters the value of the host needs to be before a typeahead is displayed.
     * Use `0` to have it display when focussing the host (clicking or tabbing into it).
     */
    typeaheadMinLength = 1;
    /**
     * Defines the name of the field/property the option string is in when the typeahead options are objects.
     */
    typeaheadOptionField = 'name';
    /**
     * Defines whether multiselection of typeahead is possible with checkboxes.
     */
    typeaheadMultiSelect = false;
    /**
     * Defines whether to tokenize the search or match the whole search.
     */
    typeaheadTokenize = true;
    /**
     * Defines whether and how to require to match with all the tokens if {@link typeaheadTokenize} is enabled.
     * - `no` does not require all of the tokens to match.
     * - `once` requires all of the parts to be found in the search.
     * - `separately` requires all of the parts to be found in the search where there is not an overlapping different result.
     * - `independently` requires all of the parts to be found in the search where there is not an overlapping or adjacent different result.
     *   ('independently' also slightly changes sorting behavior in the same way.)
     */
    typeaheadMatchAllTokens = 'separately';
    /**
     * Defines an optional template to use as the typeahead match item instead of the one built in.
     * Gets the {@link TypeaheadOptionItemContext} passed to it.
     */
    typeaheadItemTemplate;
    /**
     * @deprecated This property has no effect. The typeahead will always automatically calculate its position.
     */
    typeaheadDropup = false;
    /**
     * Skip the sorting of matches.
     * If the value is `true`, the matches are sorted according to {@link SiTypeaheadSorting}.
     */
    typeaheadSkipSortingMatches = false;
    /**
     * Screen reader only label for the autocomplete list.
     */
    typeaheadAutocompleteListLabel = $localize `:@@SI_TYPEAHEAD.AUTOCOMPLETE_LIST_LABEL:Suggestions`;
    /**
     * Emits an Event when the input field is changed.
     */
    typeaheadOnInput = new EventEmitter();
    /**
     * Emits an Event when a typeahead match is selected.
     * The event is a {@link TypeaheadMatch}
     */
    typeaheadOnSelect = new EventEmitter(true);
    /**
     * Emits an Event when a multiselect is enabled and closed using enter.
     */
    typeaheadOnMultiselectClose = new EventEmitter();
    /**
     * Emits an Event when a typeahead full match exists. A full match occurs when the entered text
     * is equal to one of the typeahead options.
     * The event is a {@link TypeaheadMatch}
     */
    typeaheadOnFullMatch = new EventEmitter(true);
    /**
     * Emits when the typeahead closed
     */
    typeaheadClosed = new EventEmitter(true);
    // The offset on the side based on the icon of the input field.
    /** @internal */
    iconOffset = 0;
    overlay = inject(Overlay);
    elementRef = inject(ElementRef);
    ngControl = inject(NgControl);
    cdRef = inject(ChangeDetectorRef);
    injector = inject(Injector);
    autoComplete = inject(SiAutocompleteDirective);
    $typeahead = new ReplaySubject(1);
    componentRef;
    component;
    inputTimer;
    sourceSubscription;
    subscription;
    matchSorter = new SiTypeaheadSorting();
    overlayRef;
    // Every time the main input changes, detect whether it is async and if it is not make an observable out of the array.
    ngOnChanges(changes) {
        if (changes.siTypeahead) {
            this.sourceSubscription?.unsubscribe();
            if (isObservable(this.siTypeahead)) {
                this.sourceSubscription = this.siTypeahead.subscribe(this.$typeahead);
            }
            else {
                this.$typeahead.next(this.siTypeahead);
            }
        }
    }
    // Clear the current input timeout (if set) and remove the component when the focus of the host is lost.
    onBlur() {
        this.clearTimer();
        if (this.component) {
            this.removeComponent();
        }
    }
    // Start the input timeout to display the typeahead when the host is focussed or a value is inputted into it.
    onInput(event) {
        const target = event.target;
        if (!target) {
            return;
        }
        // Get the value or otherwise textContent of the host element now, because later it could be reset.
        const firstValue = target.value || target.textContent;
        if (!this.inputTimer) {
            this.inputTimer = setTimeout(() => {
                this.inputTimer = undefined;
                const value = (target.value || target.textContent) ?? firstValue;
                // The value needs to fullfil the minimum length requirement set.
                if (value !== null && value.length >= this.typeaheadMinLength) {
                    if (this.component) {
                        this.setMatchesAndQuery(value, this.$typeahead, this.component);
                    }
                    else {
                        this.loadComponent(value);
                    }
                }
                else if (this.component) {
                    // If the value is too short, check if a typeahead still exists and remove it if it does.
                    this.removeComponent();
                }
                this.typeaheadOnInput.emit(value ?? '');
            }, this.typeaheadWaitMs);
        }
    }
    onKeydownEscape() {
        if (this.typeaheadCloseOnEsc) {
            this.clearTimer();
            if (this.component) {
                this.removeComponent();
            }
        }
    }
    onKeydownSpace(event) {
        if (this.typeaheadMultiSelect) {
            // Avoid space character to be inserted into the input field
            event.preventDefault();
            this.selectMatch(this.autoComplete.active?.value);
        }
    }
    ngOnDestroy() {
        this.clearTimer();
        this.sourceSubscription?.unsubscribe();
        this.subscription?.unsubscribe();
        this.overlayRef?.dispose();
    }
    // Dynamically create the typeahead component and then set the matches and the query.
    loadComponent(value) {
        if (!this.overlayRef?.hasAttached()) {
            this.overlayRef?.dispose();
            this.overlayRef = getOverlay(this.elementRef, this.overlay, false, SiTypeaheadDirective.overlayPositions, true, false);
        }
        if (this.overlayRef.hasAttached()) {
            return;
        }
        const typeaheadPortal = new ComponentPortal(SiTypeaheadComponent, null, this.injector);
        this.componentRef = this.overlayRef.attach(typeaheadPortal);
        this.component = this.componentRef.instance;
        const input = this.elementRef.nativeElement.querySelector('input');
        this.iconOffset = input ? parseFloat(getComputedStyle(input).paddingLeft) : 0;
        // Wait for change detection to prevent setting the matches before the component has subscribed to it.
        this.cdRef.detectChanges();
        this.setMatchesAndQuery(value, this.$typeahead, this.component);
    }
    // Get the matches and push them to the subject of the component, then set the query of the component.
    setMatchesAndQuery(query, items, component) {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = this.getMatches(items, query).subscribe(matches => {
            component.matches.next(matches);
            const escapedQuery = this.escapeRegex(query);
            const equalsExp = new RegExp(`\^${escapedQuery}\$`, 'i');
            const fullMatches = matches.filter(match => match.result.length === 1 && equalsExp.test(match.text));
            if (fullMatches.length > 0) {
                this.typeaheadOnFullMatch.emit(fullMatches[0]);
            }
        });
        component.query = query;
        this.component?.cdRef.markForCheck();
    }
    // If the typeahead options are objects, pick the specified field/property.
    getOptionValue(option, field) {
        return typeof option !== 'object' ? option.toString() : option[field] || '';
    }
    // If enabled, process the matches and sort through them.
    getMatches(observableList, query) {
        try {
            const entireQueryRegex = new RegExp(this.escapeRegex(query), 'gi');
            const queryParts = this.typeaheadTokenize
                ? query.split(/\s+/g).filter(queryPart => queryPart)
                : query
                    ? [query]
                    : [];
            const queryRegexes = queryParts.map(queryPart => new RegExp(this.escapeRegex(queryPart), 'gi'));
            return observableList.pipe(map(options => {
                // Check if the options need to be processed, if not just return an unprocessed object.
                if (!this.typeaheadProcess) {
                    return options.map(option => {
                        const optionValue = this.getOptionValue(option, this.typeaheadOptionField);
                        const itemSelected = this.typeaheadMultiSelect
                            ? this.getOptionValue(option, 'selected')
                            : false;
                        const iconClass = this.getOptionValue(option, 'iconClass');
                        return {
                            option,
                            itemSelected,
                            text: optionValue,
                            iconClass,
                            result: optionValue
                                ? [{ text: optionValue, isMatching: false, matches: 0, uniqueMatches: 0 }]
                                : [],
                            stringMatch: false,
                            atBeginning: false,
                            matches: 0,
                            uniqueMatches: 0,
                            uniqueSeparateMatches: 0,
                            matchesEntireQuery: false,
                            matchesAllParts: false,
                            matchesAllPartsSeparately: false,
                            active: false
                        };
                    });
                }
                else {
                    // Process the options.
                    const matches = [];
                    options.forEach(option => {
                        const optionValue = this.getOptionValue(option, this.typeaheadOptionField);
                        const stringMatch = optionValue.toLocaleLowerCase().trim() === query.toLocaleLowerCase().trim();
                        const itemSelected = this.typeaheadMultiSelect
                            ? option['selected']
                            : false;
                        const iconClass = this.getOptionValue(option, 'iconClass');
                        const candidate = {
                            option,
                            itemSelected,
                            text: optionValue,
                            iconClass,
                            result: [],
                            stringMatch,
                            atBeginning: false,
                            matches: 0,
                            uniqueMatches: 0,
                            uniqueSeparateMatches: 0,
                            matchesEntireQuery: false,
                            matchesAllParts: false,
                            matchesAllPartsSeparately: false
                        };
                        // Only search the options if a part of the query is at least one character long to prevent an endless loop.
                        if (queryParts.length === 0) {
                            if (optionValue) {
                                candidate.result.push({
                                    text: optionValue,
                                    isMatching: false,
                                    matches: 0,
                                    uniqueMatches: 0
                                });
                            }
                            matches.push(candidate);
                        }
                        else {
                            const allResults = [];
                            const allIndexes = [];
                            candidate.matchesEntireQuery = !!optionValue.match(entireQueryRegex);
                            // Loop through the option value to find multiple matches, then store every segment (matching or non-matching) in the results.
                            queryRegexes.forEach((queryRegex, index) => {
                                let regexMatch = queryRegex.exec(optionValue);
                                while (regexMatch) {
                                    allResults.push({
                                        index,
                                        start: regexMatch.index,
                                        end: regexMatch.index + regexMatch[0].length,
                                        result: regexMatch[0]
                                    });
                                    if (!regexMatch.index) {
                                        candidate.atBeginning = true;
                                    }
                                    if (!allIndexes.includes(index)) {
                                        allIndexes.push(index);
                                    }
                                    regexMatch = queryRegex.exec(optionValue);
                                }
                            });
                            candidate.matchesAllParts = allIndexes.length === queryParts.length;
                            // Check if all parts of the query match at least once (if required).
                            if (this.typeaheadMatchAllTokens === 'no' || candidate.matchesAllParts) {
                                const combinedResults = [];
                                // First combine intersecting (or if set to independently adjacent) results to combined results.
                                // We achieve this by first sorting them by the starting index, then by the ending index and then looking for overlaps.
                                allResults
                                    .sort((a, b) => a.start - b.start || a.end - b.end)
                                    .forEach(result => {
                                    if (combinedResults.length) {
                                        const foundPreviousResult = combinedResults.find(previousResult => this.typeaheadMatchAllTokens === 'independently'
                                            ? result.start <= previousResult.end
                                            : result.start < previousResult.end);
                                        if (foundPreviousResult) {
                                            foundPreviousResult.result += result.result.slice(foundPreviousResult.end - result.start, result.result.length);
                                            if (result.end > foundPreviousResult.end) {
                                                foundPreviousResult.end = result.end;
                                            }
                                            foundPreviousResult.indexes.push(result.index);
                                            if (!foundPreviousResult.uniqueIndexes.includes(result.index)) {
                                                foundPreviousResult.uniqueIndexes.push(result.index);
                                            }
                                            return;
                                        }
                                    }
                                    combinedResults.push({
                                        ...result,
                                        indexes: [result.index],
                                        uniqueIndexes: [result.index]
                                    });
                                });
                                // Recursively go through all unique combinations of the unique indexes to get the option which has the most indexes.
                                const countUniqueSubindexes = (indexIndex = 0, previousIndexes = []) => indexIndex === combinedResults.length
                                    ? previousIndexes.length
                                    : Math.max(previousIndexes.length, ...combinedResults[indexIndex].uniqueIndexes
                                        .filter(index => !previousIndexes.includes(index))
                                        .map(index => countUniqueSubindexes(indexIndex + 1, [index, ...previousIndexes])));
                                candidate.uniqueSeparateMatches = countUniqueSubindexes();
                                candidate.matchesAllPartsSeparately =
                                    candidate.uniqueSeparateMatches === queryParts.length;
                                let currentPreviousEnd = 0;
                                // Add the combined results to the candidate including the non-matching parts in between.
                                combinedResults.forEach(result => {
                                    const textBefore = optionValue.slice(currentPreviousEnd, result.start);
                                    if (textBefore) {
                                        candidate.result.push({
                                            text: textBefore,
                                            isMatching: false,
                                            matches: 0,
                                            uniqueMatches: 0
                                        });
                                    }
                                    candidate.result.push({
                                        text: result.result,
                                        isMatching: true,
                                        matches: result.indexes.length,
                                        uniqueMatches: result.uniqueIndexes.length
                                    });
                                    currentPreviousEnd = result.end;
                                    candidate.matches += result.indexes.length;
                                    candidate.uniqueMatches += result.uniqueIndexes.length;
                                });
                                // Check if there are result segments and all parts are matched independently (if required).
                                if (candidate.result.length !== 0 &&
                                    ((this.typeaheadMatchAllTokens !== 'separately' &&
                                        this.typeaheadMatchAllTokens !== 'independently') ||
                                        candidate.matchesAllPartsSeparately)) {
                                    const textAtEnd = optionValue.slice(currentPreviousEnd);
                                    if (textAtEnd) {
                                        candidate.result.push({
                                            text: textAtEnd,
                                            isMatching: false,
                                            matches: 0,
                                            uniqueMatches: 0
                                        });
                                    }
                                    matches.push(candidate);
                                }
                            }
                        }
                    });
                    if (this.typeaheadSkipSortingMatches) {
                        return matches;
                    }
                    else {
                        return this.matchSorter.sortMatches(matches);
                    }
                }
            }));
        }
        catch {
            // Could not create regex (only in extremely rare cases, maybe even impossible), so return an empty array.
            return of([]);
        }
    }
    // Select a match, either gets called due to a enter keypress or from the component due to a click.
    /** @internal */
    selectMatch(match) {
        const value = this.typeaheadClearValueOnSelect ? '' : match.text;
        match.itemSelected = !match.itemSelected;
        if (!this.typeaheadMultiSelect) {
            this.ngControl.control.setValue(value);
        }
        // Clear the current input timeout (if set) and remove the typeahead.
        this.clearTimer();
        if (this.component) {
            this.typeaheadOnSelect.emit(match);
            if (!this.typeaheadMultiSelect) {
                this.removeComponent();
            }
        }
    }
    // Remove the component by clearing the viewContainerRef
    removeComponent() {
        this.typeaheadClosed.emit();
        if (this.overlayRef?.hasAttached()) {
            this.overlayRef?.detach();
        }
        this.componentRef?.destroy();
        this.componentRef = undefined;
        this.component = undefined;
    }
    clearTimer() {
        if (this.inputTimer) {
            clearTimeout(this.inputTimer);
            this.inputTimer = undefined;
        }
    }
    escapeRegex(query) {
        return query.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiTypeaheadDirective, selector: "[siTypeahead]", inputs: { siTypeahead: "siTypeahead", typeaheadProcess: "typeaheadProcess", typeaheadScrollable: "typeaheadScrollable", typeaheadOptionsInScrollableView: "typeaheadOptionsInScrollableView", typeaheadOptionsLimit: "typeaheadOptionsLimit", typeaheadScrollableAdditionalHeight: "typeaheadScrollableAdditionalHeight", typeaheadAutoSelectIndex: "typeaheadAutoSelectIndex", typeaheadCloseOnEsc: "typeaheadCloseOnEsc", typeaheadClearValueOnSelect: "typeaheadClearValueOnSelect", typeaheadWaitMs: "typeaheadWaitMs", typeaheadMinLength: "typeaheadMinLength", typeaheadOptionField: "typeaheadOptionField", typeaheadMultiSelect: "typeaheadMultiSelect", typeaheadTokenize: "typeaheadTokenize", typeaheadMatchAllTokens: "typeaheadMatchAllTokens", typeaheadItemTemplate: "typeaheadItemTemplate", typeaheadDropup: "typeaheadDropup", typeaheadSkipSortingMatches: "typeaheadSkipSortingMatches", typeaheadAutocompleteListLabel: "typeaheadAutocompleteListLabel" }, outputs: { typeaheadOnInput: "typeaheadOnInput", typeaheadOnSelect: "typeaheadOnSelect", typeaheadOnMultiselectClose: "typeaheadOnMultiselectClose", typeaheadOnFullMatch: "typeaheadOnFullMatch", typeaheadClosed: "typeaheadClosed" }, host: { listeners: { "focusout": "onBlur()", "focusin": "onInput($event)", "input": "onInput($event)", "paste": "onInput($event)", "keydown.escape": "onKeydownEscape()", "keydown.space": "onKeydownSpace($event)" }, classAttribute: "si-typeahead" }, exportAs: ["si-typeahead"], usesOnChanges: true, hostDirectives: [{ directive: i2.SiAutocompleteDirective }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siTypeahead]',
                    exportAs: 'si-typeahead',
                    hostDirectives: [SiAutocompleteDirective],
                    host: {
                        class: 'si-typeahead'
                    }
                }]
        }], propDecorators: { siTypeahead: [{
                type: Input,
                args: [{ required: true }]
            }], typeaheadProcess: [{
                type: Input
            }], typeaheadScrollable: [{
                type: Input
            }], typeaheadOptionsInScrollableView: [{
                type: Input
            }], typeaheadOptionsLimit: [{
                type: Input
            }], typeaheadScrollableAdditionalHeight: [{
                type: Input
            }], typeaheadAutoSelectIndex: [{
                type: Input
            }], typeaheadCloseOnEsc: [{
                type: Input
            }], typeaheadClearValueOnSelect: [{
                type: Input
            }], typeaheadWaitMs: [{
                type: Input
            }], typeaheadMinLength: [{
                type: Input
            }], typeaheadOptionField: [{
                type: Input
            }], typeaheadMultiSelect: [{
                type: Input
            }], typeaheadTokenize: [{
                type: Input
            }], typeaheadMatchAllTokens: [{
                type: Input
            }], typeaheadItemTemplate: [{
                type: Input
            }], typeaheadDropup: [{
                type: Input
            }], typeaheadSkipSortingMatches: [{
                type: Input
            }], typeaheadAutocompleteListLabel: [{
                type: Input
            }], typeaheadOnInput: [{
                type: Output
            }], typeaheadOnSelect: [{
                type: Output
            }], typeaheadOnMultiselectClose: [{
                type: Output
            }], typeaheadOnFullMatch: [{
                type: Output
            }], typeaheadClosed: [{
                type: Output
            }], onBlur: [{
                type: HostListener,
                args: ['focusout']
            }], onInput: [{
                type: HostListener,
                args: ['focusin', ['$event']]
            }, {
                type: HostListener,
                args: ['input', ['$event']]
            }, {
                type: HostListener,
                args: ['paste', ['$event']]
            }], onKeydownEscape: [{
                type: HostListener,
                args: ['keydown.escape']
            }], onKeydownSpace: [{
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }] } });

class SiTypeaheadComponent {
    cdRef = inject(ChangeDetectorRef);
    parent = inject(SiTypeaheadDirective);
    matches = new BehaviorSubject([]);
    matchesArray = [];
    query = '';
    // The offset on the side based on the icon of the input field.
    iconOffset = 0;
    multiselect = false;
    // Store the number of rendered matches to know if the height needs to be recalculated.
    renderedMatchesLength = 0;
    subscription;
    typeaheadElement;
    autocompleteDirective = inject(SiAutocompleteDirective);
    ngOnInit() {
        this.multiselect = !!this.parent.typeaheadMultiSelect;
        this.iconOffset = this.parent.iconOffset;
        // Subscribe to the matches and store them in an array, also reset the active index to prevent conflicts.
        this.subscription = this.matches.subscribe(matches => {
            this.matchesArray = this.parent.typeaheadOptionsLimit
                ? matches.slice(0, this.parent.typeaheadOptionsLimit)
                : matches.slice();
            this.cdRef.markForCheck();
        });
    }
    ngAfterViewInit() {
        this.setHeight(this.typeaheadElement);
        this.renderedMatchesLength = this.matchesArray.length;
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    onMouseDown(event) {
        event.preventDefault();
    }
    trackByIndex = (index) => index;
    /*
     * Set the height of the element passed to it (typeahead) if there are items displayed,
     * the number of displayed items changed and it is scrollable.
     */
    setHeight(element) {
        if (this.matchesArray.length !== 0 && this.matchesArray.length !== this.renderedMatchesLength) {
            if (this.parent.typeaheadScrollable &&
                this.parent.typeaheadOptionsInScrollableView < this.matchesArray.length) {
                const computedStyle = getComputedStyle(element.nativeElement);
                const matchComputedStyle = getComputedStyle(element.nativeElement.firstElementChild);
                const matchHeight = parseFloat(matchComputedStyle.height || '0');
                const paddingTop = parseFloat(computedStyle.paddingTop || '0');
                const paddingBottom = parseFloat(computedStyle.paddingBottom || '');
                const height = this.parent.typeaheadOptionsInScrollableView * matchHeight;
                element.nativeElement.style.maxBlockSize = `${height + paddingTop + paddingBottom + this.parent.typeaheadScrollableAdditionalHeight}px`;
            }
            else {
                element.nativeElement.style.maxBlockSize = 'auto';
            }
        }
    }
    // Gets called when a match is selected by clicking on it.
    selectMatch(match) {
        this.parent.selectMatch(match);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiTypeaheadComponent, selector: "si-typeahead", host: { listeners: { "mousedown": "onMouseDown($event)" } }, viewQueries: [{ propertyName: "typeaheadElement", first: true, predicate: ["typeahead"], descendants: true, static: true }], ngImport: i0, template: "<!-- Template to be used for every match, can be replaced using an input. -->\n<ng-template #defaultItemTemplate let-match=\"match\">\n  <input\n    *ngIf=\"multiselect\"\n    type=\"checkbox\"\n    class=\"form-check-input me-5\"\n    tabindex=\"-1\"\n    [checked]=\"match.itemSelected\"\n  />\n  <span *ngIf=\"match.iconClass\" class=\"icon me-2\" [ngClass]=\"match.iconClass\"></span>\n  <span\n    *ngFor=\"let segment of match.result; trackBy: trackByIndex\"\n    [class.typeahead-match-segment-matching]=\"segment.isMatching\"\n    >{{ segment.text }}</span\n  >\n</ng-template>\n\n<!-- Only display the component if there are any matches and set the CSS transform to properly position the typeahead -->\n<ul\n  #typeahead\n  class=\"typeahead dropdown-menu\"\n  [siAutocompleteListboxFor]=\"autocompleteDirective\"\n  [siAutocompleteDefaultIndex]=\"parent.typeaheadAutoSelectIndex\"\n  [style.margin-inline-start.px]=\"iconOffset\"\n  [attr.aria-label]=\"parent.typeaheadAutocompleteListLabel\"\n  [class.d-none]=\"!matchesArray.length\"\n  (siAutocompleteOptionSubmitted)=\"selectMatch($event)\"\n>\n  <!-- Loop through every match and bind events, the mousedown prevent default is to prevent the host from losing focus on click -->\n  <li\n    *ngFor=\"let match of matchesArray; let index = index; trackBy: trackByIndex\"\n    #typeaheadMatch\n    class=\"dropdown-item me-4\"\n    [siAutocompleteOption]=\"match\"\n    [attr.aria-label]=\"match.text\"\n    (click)=\"$event.stopPropagation()\"\n    (mousedown)=\"$event.preventDefault()\"\n  >\n    <!-- Display either a template set as the input or the template above -->\n    <ng-template\n      [ngTemplateOutlet]=\"parent.typeaheadItemTemplate || defaultItemTemplate\"\n      [ngTemplateOutletContext]=\"{ item: match.option, index: index, match: match, query: query }\"\n    >\n    </ng-template>\n  </li>\n</ul>\n", styles: [".dropdown-menu{display:block;position:relative;inset-block-start:0;inset-inline-start:0;margin-block-start:1px;overflow-y:auto;overflow-x:hidden;max-block-size:100%}.typeahead-match-segment-matching{font-weight:700}.dropdown-item *{flex-shrink:0}.dropdown-item span{white-space:pre-wrap}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.SiAutocompleteListboxDirective, selector: "[siAutocompleteListboxFor]", inputs: ["id", "siAutocompleteListboxFor", "siAutocompleteDefaultIndex"], outputs: ["siAutocompleteOptionSubmitted"], exportAs: ["siAutocompleteListbox"] }, { kind: "directive", type: i2.SiAutocompleteOptionDirective, selector: "[siAutocompleteOption]", inputs: ["id", "disabled", "siAutocompleteOption"], exportAs: ["siAutocompleteOption"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-typeahead', changeDetection: ChangeDetectionStrategy.OnPush, template: "<!-- Template to be used for every match, can be replaced using an input. -->\n<ng-template #defaultItemTemplate let-match=\"match\">\n  <input\n    *ngIf=\"multiselect\"\n    type=\"checkbox\"\n    class=\"form-check-input me-5\"\n    tabindex=\"-1\"\n    [checked]=\"match.itemSelected\"\n  />\n  <span *ngIf=\"match.iconClass\" class=\"icon me-2\" [ngClass]=\"match.iconClass\"></span>\n  <span\n    *ngFor=\"let segment of match.result; trackBy: trackByIndex\"\n    [class.typeahead-match-segment-matching]=\"segment.isMatching\"\n    >{{ segment.text }}</span\n  >\n</ng-template>\n\n<!-- Only display the component if there are any matches and set the CSS transform to properly position the typeahead -->\n<ul\n  #typeahead\n  class=\"typeahead dropdown-menu\"\n  [siAutocompleteListboxFor]=\"autocompleteDirective\"\n  [siAutocompleteDefaultIndex]=\"parent.typeaheadAutoSelectIndex\"\n  [style.margin-inline-start.px]=\"iconOffset\"\n  [attr.aria-label]=\"parent.typeaheadAutocompleteListLabel\"\n  [class.d-none]=\"!matchesArray.length\"\n  (siAutocompleteOptionSubmitted)=\"selectMatch($event)\"\n>\n  <!-- Loop through every match and bind events, the mousedown prevent default is to prevent the host from losing focus on click -->\n  <li\n    *ngFor=\"let match of matchesArray; let index = index; trackBy: trackByIndex\"\n    #typeaheadMatch\n    class=\"dropdown-item me-4\"\n    [siAutocompleteOption]=\"match\"\n    [attr.aria-label]=\"match.text\"\n    (click)=\"$event.stopPropagation()\"\n    (mousedown)=\"$event.preventDefault()\"\n  >\n    <!-- Display either a template set as the input or the template above -->\n    <ng-template\n      [ngTemplateOutlet]=\"parent.typeaheadItemTemplate || defaultItemTemplate\"\n      [ngTemplateOutletContext]=\"{ item: match.option, index: index, match: match, query: query }\"\n    >\n    </ng-template>\n  </li>\n</ul>\n", styles: [".dropdown-menu{display:block;position:relative;inset-block-start:0;inset-inline-start:0;margin-block-start:1px;overflow-y:auto;overflow-x:hidden;max-block-size:100%}.typeahead-match-segment-matching{font-weight:700}.dropdown-item *{flex-shrink:0}.dropdown-item span{white-space:pre-wrap}\n"] }]
        }], propDecorators: { typeaheadElement: [{
                type: ViewChild,
                args: ['typeahead', { static: true }]
            }], onMouseDown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

class SiTypeaheadModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadModule, declarations: [SiTypeaheadComponent, SiTypeaheadDirective], imports: [CommonModule,
            OverlayModule,
            SiAutocompleteListboxDirective,
            SiAutocompleteOptionDirective,
            SiTranslateModule], exports: [SiTypeaheadDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadModule, imports: [CommonModule,
            OverlayModule,
            SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTypeaheadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        OverlayModule,
                        SiAutocompleteListboxDirective,
                        SiAutocompleteOptionDirective,
                        SiTranslateModule
                    ],
                    declarations: [SiTypeaheadComponent, SiTypeaheadDirective],
                    exports: [SiTypeaheadDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiTypeaheadComponent, SiTypeaheadDirective, SiTypeaheadModule };
